<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { normandyOutline, normandyTowns } from '~/data/normandy'

const { t } = useI18n()
const localePath = useLocalePath()

const town = (name: string) => normandyTowns[name]!

const stops = computed(() => [
  { name: 'Caen', detail: 'Calvados (14)', at: town('Caen'), side: 'right', coast: false },
  { name: 'Coutances', to: localePath('/creation-site-web-coutances'), at: town('Coutances'), side: 'right', coast: false },
  { name: 'Agon-Coutainville', to: localePath('/creation-site-web-agon-coutainville'), at: town('Agon-Coutainville'), side: 'left', coast: true },
  { name: 'Blainville-sur-Mer', to: localePath('/creation-site-web-blainville-sur-mer'), at: town('Blainville-sur-Mer'), side: 'left', coast: true },
  { name: 'Gouville-sur-Mer', detail: 'Manche (50)', to: localePath('/creation-site-web-gouville-sur-mer'), at: town('Gouville-sur-Mer'), side: 'left', coast: true },
])

// The trip: a long way across Normandy, then a few kilometres along the coast.
const [caen, coutances, agon, blainville, gouville] = ['Caen', 'Coutances', 'Agon-Coutainville', 'Blainville-sur-Mer', 'Gouville-sur-Mer'].map(town) as [number, number][]
const legs = [
  `C${caen[0] - 20} ${caen[1] + 22}, ${coutances[0] + 28} ${coutances[1] - 14}, ${coutances[0]} ${coutances[1]}`,
  `C${coutances[0] - 3} ${coutances[1] + 1.5}, ${agon[0] + 4} ${agon[1] + 2}, ${agon[0]} ${agon[1]}`,
  `C${agon[0] - 1.5} ${agon[1] - 1.5}, ${blainville[0] - 1.5} ${blainville[1] + 1.5}, ${blainville[0]} ${blainville[1]}`,
  `C${blainville[0] + 1} ${blainville[1] - 1.5}, ${gouville[0] - 1.5} ${gouville[1] + 1.5}, ${gouville[0]} ${gouville[1]}`,
]
const start = `M${caen[0]} ${caen[1]}`
const route = `${start} ${legs.join(' ')}`

// The two framings of the map, in the units of the map data.
const wholeRegion = { x: -6, y: 8, w: 288 }
const coastline = { x: 14, y: 112, w: 36 }
const RATIO = 224 / 288

const smooth = (value: number) => value * value * (3 - 2 * value)
const between = (value: number, from: number, to: number) => Math.min(1, Math.max(0, (value - from) / (to - from)))

// Without animation (server render, reduced motion): the whole region, the trip up to Coutances, the photo of Caen.
const outline = ref(1)
const progress = ref(0.5)
const coutancesAt = ref(0.8)

const zoom = computed(() => smooth(between(progress.value, 0.5, 0.72)))
const view = computed(() => {
  const w = wholeRegion.w * (coastline.w / wholeRegion.w) ** zoom.value
  const focus = (w - coastline.w) / (wholeRegion.w - coastline.w)
  return {
    w,
    h: w * RATIO,
    x: coastline.x + (wholeRegion.x - coastline.x) * focus,
    y: coastline.y + (wholeRegion.y - coastline.y) * focus,
  }
})
const viewBox = computed(() => `${view.value.x} ${view.value.y} ${view.value.w} ${view.value.h}`)

// Share of the route already travelled.
const travelled = computed(() => {
  const across = between(progress.value, 0.05, 0.55) * coutancesAt.value
  const along = between(progress.value, 0.62, 0.97) * (1 - coutancesAt.value)
  return across + along
})

// Each photo sits on the map beside its town: Caen during the crossing, Gouville once the zoom has landed.
const caenCard = computed(() => between(progress.value, 0.06, 0.16) * (1 - between(progress.value, 0.5, 0.58)))
const gouvilleCard = computed(() => between(progress.value, 0.72, 0.8))
const unit = computed(() => view.value.w / 800)

// Once zoomed in, the map has no edge of its own: it dissolves into the page.
const edgeFade = computed(() => zoom.value > 0
  ? `radial-gradient(ellipse farthest-corner at 50% 50%, black ${100 - 55 * zoom.value}%, transparent 100%)`
  : 'none')

const routePath = ref<SVGPathElement>()
const point = ref<[number, number]>(coutances)
const reached = ref(2)

const position = (at: [number, number]) => ({
  left: `${((at[0] - view.value.x) / view.value.w) * 100}%`,
  top: `${((at[1] - view.value.y) / view.value.h) * 100}%`,
})

const root = ref<HTMLElement>()
let media: gsap.MatchMedia | undefined

function animateMap() {
  const path = routePath.value!
  const total = path.getTotalLength()
  const fractions = legs.map((_, index) => {
    const part = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    part.setAttribute('d', `${start} ${legs.slice(0, index + 1).join(' ')}`)
    return part.getTotalLength() / total
  })
  coutancesAt.value = fractions[0]!

  const state = { progress: 0, outline: 0 }
  const render = () => {
    outline.value = state.outline
    progress.value = state.progress
    const here = path.getPointAtLength(travelled.value * total)
    point.value = [here.x, here.y]
    reached.value = fractions.filter(fraction => travelled.value >= fraction - 0.002).length
  }
  render()

  const track = root.value!.querySelector('.map-track')
  const tweens = [
    // the region draws itself while the section arrives on screen
    gsap.to(state, { outline: 1, ease: 'none', onUpdate: render, scrollTrigger: { trigger: track, start: 'top 85%', end: 'top top', scrub: 0.4 } }),
    gsap.to(state, { progress: 1, ease: 'none', onUpdate: render, scrollTrigger: { trigger: track, start: 'top top', end: 'bottom bottom', scrub: 0.4 } }),
  ]

  return () => {
    tweens.forEach(tween => tween.kill())
    outline.value = 1
    progress.value = 0.5
    point.value = coutances
    reached.value = 2
  }
}

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger)
  media = gsap.matchMedia()
  media.add('(prefers-reduced-motion: no-preference)', animateMap, root.value)
})

onBeforeUnmount(() => media?.revert())
</script>

<template>
  <section
    ref="root"
    class="mx-auto mt-16 w-full max-w-6xl text-left"
  >
    <div class="map-track relative h-[260vh] overflow-x-clip">
      <div class="sticky top-0 h-screen">
        <!-- the text sits in front of the map -->
        <div class="relative z-10 max-w-lg pt-6 sm:pt-20">
          <h2 class="text-balance text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl">
            {{ t('agency.why_local') }}
          </h2>
          <div class="mt-5 space-y-3 leading-relaxed text-white/70">
            <p>{{ t('agency.why_local_text') }}</p>
            <p>{{ t('agency.road_text') }}</p>
          </div>
          <UButton
            :to="localePath('/contact')"
            size="lg"
            color="primary"
            variant="solid"
            :label="t('agency.cta')"
            trailing-icon="i-heroicons-arrow-right-20-solid"
            class="mt-6"
          />
        </div>

        <!-- the map, bottom right, without any frame -->
        <div class="absolute bottom-16 right-0 aspect-[288/224] w-full sm:bottom-4 lg:w-[min(78%,calc((100vh-5rem)*1.2857))]">
            <svg
              :viewBox="viewBox"
              fill="none"
              aria-hidden="true"
              class="absolute inset-0 size-full"
              :style="{ maskImage: edgeFade, WebkitMaskImage: edgeFade }"
            >
              <path
                :d="normandyOutline"
                pathLength="1"
                stroke="white"
                stroke-opacity="0.45"
                :stroke-width="1.2 * unit"
                stroke-dasharray="1"
                :stroke-dashoffset="1 - outline"
                fill="white"
                :fill-opacity="0.05 * outline"
                stroke-linejoin="round"
              />
              <path
                ref="routePath"
                :d="route"
                pathLength="1"
                stroke="#b9c9ae"
                :stroke-width="3 * unit"
                stroke-linecap="round"
                stroke-dasharray="1"
                :stroke-dashoffset="1 - travelled"
              />
              <circle
                v-for="(stop, index) in stops"
                :key="stop.name"
                :cx="stop.at[0]"
                :cy="stop.at[1]"
                :r="(stop.detail ? 7 : 5) * unit"
                :fill="reached >= index ? '#b9c9ae' : '#0a0a0a'"
                stroke="#b9c9ae"
                :stroke-width="1.5 * unit"
              />
              <circle
                :cx="point[0]"
                :cy="point[1]"
                :r="18 * unit"
                fill="#b9c9ae"
                opacity="0.25"
              />
              <circle
                :cx="point[0]"
                :cy="point[1]"
                :r="8 * unit"
                fill="white"
              />
            </svg>

            <!-- the stops: real links, following the zoom. The coast towns only appear once it is close enough to read them. -->
            <div
              v-for="(stop, index) in stops"
              :key="stop.name"
              class="absolute -translate-y-1/2"
              :class="stop.side === 'left' ? '-translate-x-full pr-5 text-right' : 'pl-5'"
              :style="{
                ...position(stop.at),
                opacity: (stop.coast ? zoom : stop.detail ? 1 - between(zoom, 0, 0.3) : 1) * (reached >= index ? 1 : 0.4),
                pointerEvents: stop.coast && zoom < 0.5 ? 'none' : undefined,
              }"
            >
              <NuxtLink
                v-if="stop.to"
                :to="stop.to"
                class="block whitespace-nowrap font-semibold tracking-tight text-white hover:underline"
                :class="stop.detail ? 'text-lg sm:text-2xl' : 'text-sm sm:text-lg'"
              >
                {{ stop.name }}
              </NuxtLink>
              <span
                v-else
                class="block whitespace-nowrap text-lg font-semibold tracking-tight text-white sm:text-2xl"
              >
                {{ stop.name }}
              </span>
              <span
                v-if="stop.detail"
                class="text-xs text-white/60 sm:text-sm"
              >{{ stop.detail }}</span>
            </div>

            <!-- the photos live on the map, beside their town -->
            <div
              class="pointer-events-none absolute left-[50%] top-0 aspect-[3/2] w-[36%] lg:left-[46%] lg:top-[8%] overflow-hidden rounded-lg border border-white/20 shadow-2xl shadow-black/60 lg:w-[34%]"
              :style="{ opacity: caenCard, transform: `translateY(${(1 - caenCard) * 12}px)` }"
            >
              <NuxtImg
                src="/caen.jpeg"
                width="640"
                densities="x1"
                loading="lazy"
                alt="Caen"
                class="size-full object-cover"
              />
            </div>
            <div
              class="pointer-events-none absolute left-[58%] top-[4%] aspect-[3/2] w-[40%] overflow-hidden rounded-lg border border-white/20 shadow-2xl shadow-black/60 lg:w-[34%]"
              :style="{ opacity: gouvilleCard, transform: `translateY(${(1 - gouvilleCard) * 12}px)` }"
            >
              <NuxtImg
                src="/gouville.jpeg"
                width="640"
                densities="x1"
                loading="lazy"
                alt="Gouville-sur-Mer"
                class="size-full object-cover"
              />
            </div>
          </div>
      </div>
    </div>
  </section>
</template>
